/**
 * Route prefix utilities for dynamic flow-based routing
 */

/**
 * Prefixes a route path with the given prefix
 *
 * Intelligently combines a prefix with a route, handling edge cases like:
 * - Empty prefixes (returns route as-is)
 * - Routes that already start with the prefix (returns route as-is)
 *
 * Note: This function only handles the pathname part. Query parameters and hash
 * fragments should be handled separately through Next.js router's query object.
 *
 * @param route - The base route to prefix (e.g., '/login', '/summary')
 * @param prefix - The prefix to add (e.g., '/apple-paylater', or '' for no prefix)
 * @returns The prefixed route
 *
 * @example
 * ```typescript
 * prefixRoute('/login', '/apple-paylater')
 * // Returns: '/apple-paylater/login'
 *
 * prefixRoute('/login', '')
 * // Returns: '/login'
 * ```
 */
export const prefixRoute = (route: string, prefix: string): string => {
  if (prefix === '' || prefix.length === 0) {
    return route
  }

  if (route.startsWith(prefix + '/') || route === prefix) {
    return route
  }

  const normalizedPrefix = prefix.startsWith('/') ? prefix : `/${prefix}`
  const normalizedPath = route.startsWith('/') ? route : `/${route}`

  return normalizedPrefix + normalizedPath
}
